import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import {
  API_CONTRACT_REVIEW_STATUS,
  isContractDocumentFinalized,
  hasReplacementContractStarted,
  mapAdminReviewStatusToApi,
  type ConsignmentAdminReviewStatus,
} from '@/pages/users/consignmentContractUtils';

export interface UserContractInfo {
  contractId?: string | number;
  contractID?: string | number;
  id?: string | number;
  contractStatus?: string;
  reviewStatus?: string;
  status?: string;
  contractedAt?: string;
  documentNumber?: string;
  originalFileUrl?: string;
  finalPdfDownloadUrl?: string;
  finalPdfDownloadURL?: string;
  recontractRequestReason?: string;
  updatedAt?: string;
}

export interface ConsignmentContractDocument {
  signedAt?: string;
  signed_at?: string;
  signedHtmlContent?: string;
  signedHtml?: string;
  signedHtmlDownloadUrl?: string;
  signedHtmlDownloadURL?: string;
  signedHtmlPresignedUrl?: string;
  signedHtmlPresignedURL?: string;
  signedHtmlPreviewUrl?: string;
  signedHtmlPreviewURL?: string;
  finalPdfDownloadUrl?: string;
  finalPdfDownloadURL?: string;
}

export interface ConsignmentContractDetail {
  contractId?: string | number;
  contractVersion?: string | number;
  contract_version?: string | number;
  previousContractId?: string | number;
  previousContractID?: string | number;
  replacementContractId?: string | number;
  replacementContractID?: string | number;
  contractStatus?: string;
  reviewStatus?: string;
  status?: string;
  documentNumber?: string;
  updatedAt?: string;
  signedAt?: string;
  recontractRequestReason?: string;
  finalPdfDownloadUrl?: string;
  finalPdfDownloadURL?: string;
  contractDocument?: ConsignmentContractDocument;
  signedHtmlContent?: string;
  signedHtml?: string;
  signedHtmlDownloadUrl?: string;
  signedHtmlDownloadURL?: string;
  signedHtmlPresignedUrl?: string;
  signedHtmlPresignedURL?: string;
  signedHtmlPreviewUrl?: string;
  signedHtmlPreviewURL?: string;
  signedHtmlUrl?: string;
  presignedSignedHtmlUrl?: string;
  signedHtmlPresignedUrlForAdmin?: string;
}

export interface ConsignmentPendingContract {
  contractId?: string | number;
  id?: string | number;
  userId?: number;
  email?: string;
}

const normalizePendingList = (
  data: unknown,
): ConsignmentPendingContract[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.content)) return record.content;
    if (Array.isArray(record.items)) return record.items;
  }
  return [];
};

const pickPrimaryPendingRow = (
  list: ConsignmentPendingContract[],
  userId?: number,
  email?: string,
): ConsignmentPendingContract | null => {
  if (!list.length) return null;
  if (userId != null) return list[0];
  const em = email?.trim().toLowerCase() ?? '';
  for (const row of list) {
    const rem = row.email?.trim().toLowerCase() ?? '';
    if (em && rem === em) return row;
  }
  return null;
};

export const consignmentContractService = {
  getPendingContracts: async (userId?: number) => {
    const params: Record<string, number> = { page: 0, size: 100 };
    if (userId != null) params.userId = userId;
    const response = await apiClient.get(createApiPath.contracts.pending(), {
      params,
    });
    return normalizePendingList(response.data);
  },

  getContractDetail: async (
    contractId: string,
  ): Promise<ConsignmentContractDetail> => {
    const response = await apiClient.get(
      createApiPath.contracts.detail(contractId),
    );
    return response.data;
  },

  fetchSignedHtmlContent: async (contractId: string): Promise<string> => {
    const response = await apiClient.get(
      createApiPath.contracts.signedHtml(contractId),
      {
        responseType: 'text',
        headers: { Accept: 'text/html, text/plain, */*' },
        transformResponse: [(data: unknown) => data],
      },
    );
    const text = typeof response.data === 'string' ? response.data : '';
    if (!text.trim()) throw new Error('HTML 내용이 비어 있습니다.');
    return text;
  },

  /** 최종 계약 PDF presigned download URL */
  fetchFinalPdfDownloadUrl: async (contractId: string): Promise<string> => {
    const response = await apiClient.get(
      createApiPath.contracts.finalPdfDownloadUrl(contractId),
    );
    const data = response.data as Record<string, unknown> | null;
    const url =
      data?.finalPdfDownloadUrl ??
      data?.finalPdfDownloadURL ??
      data?.downloadUrl ??
      data?.downloadURL ??
      data?.url ??
      data?.presignedUrl ??
      null;
    if (url == null || String(url).trim() === '') {
      throw new Error('PDF 다운로드 URL을 확인할 수 없습니다.');
    }
    return String(url).trim();
  },

  finalizeContract: async (contractId: string) => {
    const response = await apiClient.post(
      createApiPath.contracts.finalize(contractId),
    );
    return response.data as { contractId?: string };
  },

  updateReviewStatus: async (
    contractId: string,
    body: { reviewStatus: string; recontractRequestReason?: string },
  ) => {
    const response = await apiClient.patch(
      createApiPath.contracts.reviewStatus(contractId),
      body,
    );
    return response.data as ConsignmentContractDetail;
  },

  applyConsignmentReview: async ({
    contractId,
    reviewStatusUi,
    recontractReason,
    contractDetail,
    contractInfo,
  }: {
    contractId: string;
    reviewStatusUi: ConsignmentAdminReviewStatus;
    recontractReason?: string;
    contractDetail?: ConsignmentContractDetail | null;
    contractInfo?: UserContractInfo | null;
  }) => {
    const apiReviewStatus = mapAdminReviewStatusToApi(reviewStatusUi);
    if (!apiReviewStatus) {
      throw new Error('지원하지 않는 검토 상태입니다.');
    }

    if (
      apiReviewStatus !== API_CONTRACT_REVIEW_STATUS.RECONTRACT_REQUESTED &&
      hasReplacementContractStarted(contractDetail)
    ) {
      throw new Error(
        '이미 후속 재계약이 시작되어 기존 계약의 검토 상태를 변경할 수 없습니다.',
      );
    }

    // 레거시: 검토 중(FINALIZING 등) 재계약도 /reject 없이
    // 미완료면 finalize → PATCH review-status 로 처리한다.
    let activeContractId = contractId;
    if (!isContractDocumentFinalized(contractDetail, contractInfo)) {
      const finalized = await consignmentContractService.finalizeContract(
        contractId,
      );
      activeContractId = finalized?.contractId ?? contractId;
    }

    if (apiReviewStatus === API_CONTRACT_REVIEW_STATUS.RECONTRACT_REQUESTED) {
      if (!recontractReason) {
        throw new Error('재계약 사유를 선택해 주세요.');
      }
      return consignmentContractService.updateReviewStatus(activeContractId, {
        reviewStatus: apiReviewStatus,
        recontractRequestReason: recontractReason,
      });
    }

    return consignmentContractService.updateReviewStatus(activeContractId, {
      reviewStatus: apiReviewStatus,
    });
  },
};

export const fetchConsignmentContractTabData = async ({
  userId,
  email,
  contractInfo,
}: {
  userId?: number;
  email?: string;
  contractInfo?: UserContractInfo | null;
}) => {
  const pendingList = await consignmentContractService.getPendingContracts(
    userId,
  );
  const pendingRow = pickPrimaryPendingRow(pendingList, userId, email);
  const contractIdFromInfo =
    contractInfo?.contractId ??
    contractInfo?.contractID ??
    contractInfo?.id ??
    null;
  const resolvedContractId = String(
    pendingRow?.contractId ?? pendingRow?.id ?? contractIdFromInfo ?? '',
  ).trim();

  let contractDetail: ConsignmentContractDetail | null = null;
  if (resolvedContractId) {
    contractDetail = await consignmentContractService.getContractDetail(
      resolvedContractId,
    );
  }

  return {
    pendingList,
    matchedPendingRow: pendingRow,
    resolvedContractId: resolvedContractId || null,
    contractDetail,
  };
};
